import { useState } from "react";
import { C, MONO } from "./ui-tokens";

/** One spoke of the radar: a competency at its inferred level (or N/D). */
export interface RadarAxis {
  id: string;
  label: string;
  /** Inferred level, or null when the axis abstained (collapses to center). */
  level: number | null;
  /** Self-reported confidence 0–1 (drives the filled-vs-hollow vertex dot). */
  confidence: number;
}

export interface RadarProps {
  axes: RadarAxis[];
  levels: { min: number; max: number };
  /** The chart-wide expected level range — a discipline's sub-band, uniform
   *  across every vertical competency. Drives BOTH the single shaded band
   *  region (one annulus, not a per-spoke stem) and the out-of-band marker
   *  color. Omit for the cross radar (a cross competency has no sub-band).
   *  A band equal to the full model range draws nothing — it carries no
   *  information. */
  band?: { min: number; max: number };
  size?: number;
  selectedId?: string | null;
  onSelect: (id: string) => void;
  showConfidence?: boolean;
}

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

/** Greedy word-wrap a label so it stacks across lines instead of overflowing
 *  the card. Words longer than the budget keep their own line (never split
 *  mid-word). */
function wrapLabel(label: string, maxChars = 16): string[] {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [label];
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && candidate.length > maxChars) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * Radar plotting one vertex per competency on the model's level scale. An
 * abstaining axis (`level === null`) collapses to the center with a dashed
 * spoke and NO vertex — the gap reads as "no reading", not level-zero. The
 * scored axes form a filled wedge; clicking a vertex/label selects the axis.
 */
export function Radar({
  axes,
  levels,
  band,
  size = 336,
  selectedId,
  onSelect,
  showConfidence = false,
}: RadarProps) {
  const [hover, setHover] = useState<string | null>(null);
  const c = size / 2;
  const R = c - 76;
  const n = axes.length;
  const span = Math.max(1, levels.max - levels.min);
  const ring = (v: number) => (R * v) / span;
  const angleOf = (i: number) => (i * 360) / n;
  const valOf = (a: RadarAxis) => (a.level === null ? 0 : a.level - levels.min);
  const pointOf = (i: number, a: RadarAxis) => polar(c, c, ring(valOf(a)), angleOf(i));

  // Out-of-band state of a vertex relative to the chart's expected range.
  // Drives the marker color: amber below the floor (sub-par for this function),
  // green above the ceiling, brand red in-band. No band (cross radar) → null →
  // default brand-red marker.
  const bandOf = (a: RadarAxis): "below" | "in" | "above" | null => {
    if (a.level === null || !band) return null;
    if (a.level < band.min) return "below";
    if (a.level > band.max) return "above";
    return "in";
  };
  const markColor = (a: RadarAxis) => {
    const s = bandOf(a);
    return s === "below" ? C.amberFg : s === "above" ? C.green : C.red;
  };
  const markTint = (a: RadarAxis) => {
    const s = bandOf(a);
    return s === "below" ? C.amberBg : s === "above" ? C.greenBg : C.red12;
  };

  // Interaction props shared by every clickable spoke/vertex/label group.
  // Keeps each an accessible button (role + keyboard handler) so the radar is
  // operable without a mouse.
  const interactive = (a: RadarAxis) => ({
    role: "button" as const,
    tabIndex: 0,
    "aria-label": `${a.label}: ${
      a.level === null
        ? "N/D"
        : `L${a.level}${
            bandOf(a) === "below"
              ? " (abaixo da faixa desta função)"
              : bandOf(a) === "above"
                ? " (acima da faixa desta função)"
                : ""
          }`
    }`,
    onClick: () => onSelect(a.id),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(a.id);
      }
    },
    onMouseEnter: () => setHover(a.id),
    onMouseLeave: () => setHover(null),
    style: { cursor: "pointer" },
  });

  // Contiguous runs of scored axes (split by N/D). Each N/D sector stays empty.
  const ndExists = axes.some((a) => a.level === null);
  let runs: number[][] = [];
  if (n === 0) {
    runs = [];
  } else if (!ndExists) {
    runs = [axes.map((_, i) => i)];
  } else {
    const start = axes.findIndex((a) => a.level === null);
    let cur: number[] = [];
    for (let s = 1; s <= n; s++) {
      const idx = (start + s) % n;
      if (axes[idx].level === null) {
        if (cur.length) {
          runs.push(cur);
          cur = [];
        }
      } else {
        cur.push(idx);
      }
    }
    if (cur.length) runs.push(cur);
  }

  // Ring values to draw (skip the center value = min).
  const rings: number[] = [];
  for (let v = 1; v <= span; v++) rings.push(v);

  // Discipline band region: a single shaded annulus between the function's
  // floor and ceiling level rings, spanning every axis (the band is uniform
  // per discipline). Only when a proper sub-band was supplied — a band equal to
  // the full model range carries no information → no region. When the floor
  // equals the model floor the inner ring collapses to the center and the
  // annulus becomes a filled wedge from center to the ceiling ring.
  const bandRegion =
    band && (band.min > levels.min || band.max < levels.max)
      ? {
          outer: axes.map((_, i) => polar(c, c, ring(band.max - levels.min), angleOf(i))),
          inner: axes.map((_, i) => polar(c, c, ring(band.min - levels.min), angleOf(i))),
          label: `Faixa da função: L${band.min}–L${band.max}`,
        }
      : null;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: "block", overflow: "visible" }}
      role="img"
      aria-label="Radar de competências"
    >
      {/* level rings */}
      {rings.map((rv) => (
        <polygon
          key={rv}
          points={axes.map((_, i) => polar(c, c, ring(rv), angleOf(i)).join(",")).join(" ")}
          fill={rv === span ? "rgba(31,27,23,0.015)" : "none"}
          stroke="rgba(31,27,23,0.07)"
          strokeWidth={1}
        />
      ))}
      {/* outer + inner ring labels */}
      {[1, span].map((rv) => {
        const [x, y] = polar(c, c, ring(rv), 0);
        return (
          <text
            key={rv}
            x={x + 5}
            y={y + 3}
            style={{
              fontFamily: MONO.fontFamily,
              fontVariantNumeric: MONO.fontVariantNumeric,
              fontSize: 8.5,
              fill: C.ink28,
            }}
          >
            L{levels.min + rv}
          </text>
        );
      })}
      {/* spokes */}
      {axes.map((a, i) => {
        const [x, y] = polar(c, c, R, angleOf(i));
        return (
          <line
            key={a.id}
            x1={c}
            y1={c}
            x2={x}
            y2={y}
            stroke={a.level === null ? C.ink20 : C.ink10}
            strokeWidth={1}
            strokeDasharray={a.level === null ? "3 3" : "none"}
          />
        );
      })}
      {/* discipline band — one lightly-shaded region between the function's
          floor and ceiling level rings, drawn once across all axes (the band is
          uniform per discipline, so a single zone reads better than a per-spoke
          stem). A vertex inside the zone is in-band; one outside it (the clamped
          floor−1 / ceiling+1 rung) is colored amber-below / green-above. Cross
          competencies carry no band → no zone. */}
      {bandRegion && (
        <g>
          <title>{bandRegion.label}</title>
          <path
            d={`M${bandRegion.outer.map((p) => p.join(",")).join("L")}Z M${bandRegion.inner
              .map((p) => p.join(","))
              .join("L")}Z`}
            fillRule="evenodd"
            fill={C.red5}
            stroke="none"
          />
          <polygon
            points={bandRegion.outer.map((p) => p.join(",")).join(" ")}
            fill="none"
            stroke={C.red20}
            strokeWidth={1.25}
          />
          <polygon
            points={bandRegion.inner.map((p) => p.join(",")).join(" ")}
            fill="none"
            stroke={C.red20}
            strokeWidth={1.25}
            strokeDasharray="3 3"
          />
        </g>
      )}
      {/* data wedge(s) */}
      {runs.map((run) => {
        const key = run.join("-");
        const pts = run.map((i) => pointOf(i, axes[i]));
        if (!ndExists) {
          return (
            <polygon
              key={key}
              points={pts.map((p) => p.join(",")).join(" ")}
              fill={C.red12}
              stroke={C.red}
              strokeWidth={2}
              strokeLinejoin="round"
            />
          );
        }
        const wedge: [number, number][] = [[c, c], ...pts];
        return (
          <g key={key}>
            <polygon
              points={wedge.map((p) => p.join(",")).join(" ")}
              fill={C.red12}
              stroke="none"
            />
            {pts.length > 1 && (
              <polyline
                points={pts.map((p) => p.join(",")).join(" ")}
                fill="none"
                stroke={C.red}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )}
          </g>
        );
      })}
      {/* vertices (none for N/D — invisible click target on the spoke instead) */}
      {axes.map((a, i) => {
        const active = selectedId === a.id || hover === a.id;
        if (a.level === null) {
          const [ex, ey] = polar(c, c, R, angleOf(i));
          return (
            <g key={a.id} {...interactive(a)}>
              <line x1={c} y1={c} x2={ex} y2={ey} stroke="transparent" strokeWidth={22} />
            </g>
          );
        }
        const [x, y] = pointOf(i, a);
        const high = a.confidence >= 0.75;
        const dot = markColor(a);
        return (
          <g key={a.id} {...interactive(a)}>
            <circle
              cx={x}
              cy={y}
              r={active ? 13 : 9}
              fill={markTint(a)}
              opacity={active ? 1 : 0.55}
              style={{ transition: "r 120ms, opacity 120ms" }}
            />
            <circle
              cx={x}
              cy={y}
              r={active ? 7.5 : 5}
              fill={active || high ? dot : C.white}
              stroke={dot}
              strokeWidth={2}
              style={{ transition: "r 120ms" }}
            />
            <circle cx={x} cy={y} r={20} fill="transparent" />
          </g>
        );
      })}
      {/* labels */}
      {axes.map((a, i) => {
        const [lx, ly] = polar(c, c, R + 24, angleOf(i));
        const cos = Math.cos(((angleOf(i) - 90) * Math.PI) / 180);
        const anchor = Math.abs(cos) < 0.3 ? "middle" : cos > 0 ? "start" : "end";
        const active = selectedId === a.id || hover === a.id;
        const lines = wrapLabel(a.label);
        const lineHeight = 13;
        // Sub-line sits just below the last wrapped label line; the math
        // collapses to the original single-line offsets when lines.length === 1.
        const subY = ly - 3 + (lines.length - 1) * lineHeight + 15;
        const ob = bandOf(a);
        const obSuffix =
          ob === "below" ? " · abaixo da faixa" : ob === "above" ? " · acima da faixa" : "";
        const sub =
          a.level === null
            ? "N/D"
            : (showConfidence
                ? `L${a.level} · ${Math.round(a.confidence * 100)}%`
                : `L${a.level}`) + obSuffix;
        return (
          <g key={a.id} {...interactive(a)}>
            <text
              x={lx}
              y={ly - 3}
              textAnchor={anchor}
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 11.5,
                fontWeight: 600,
                fill: active ? C.red : C.ink,
              }}
            >
              {lines.map((line, li) => (
                <tspan key={line} x={lx} dy={li === 0 ? 0 : lineHeight}>
                  {line}
                </tspan>
              ))}
            </text>
            <text
              x={lx}
              y={subY}
              textAnchor={anchor}
              style={{
                fontFamily: MONO.fontFamily,
                fontVariantNumeric: MONO.fontVariantNumeric,
                fontSize: 11,
                fontWeight: 700,
                fill:
                  a.level === null
                    ? "rgba(31,27,23,0.42)"
                    : ob === "below"
                      ? C.amberFg
                      : ob === "above"
                        ? C.greenFg
                        : "rgba(31,27,23,0.6)",
              }}
            >
              {sub}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
